import { useMemo } from 'react'
import { ValidatedLocation } from '../typings'
import {
  locationToFormModel as transform,
  validateExternalServiceLocationForm as validate,
} from '@user/components/Location/AddServiceLocationForm'
import { LocationDTO } from '@sales-center/common'
import { useLocalisedValidations } from '@user/utils/useLocalisedValidations'

function validateLocations(
  locations: LocationDTO[],
  runPestPacValidation: boolean,
  validatePostalCode: (postalCode: string) => string | undefined,
): [validated: ValidatedLocation[], someInvalid: boolean] {
  const validated = locations.map<ValidatedLocation>((loc) => {
    const errors = validate(
      transform(loc),
      runPestPacValidation,
      validatePostalCode,
    )
    const isInvalid = Object.keys(errors).length !== 0
    return { ...loc, isInvalid }
  })

  const someInvalid = validated.some((x) => x.isInvalid)

  return [validated, someInvalid]
}

type Params = {
  billToLocations: LocationDTO[]
  serviceLocations: LocationDTO[]
  runPestPacValidation: boolean
}

export function useValidateLocations(params: Params) {
  const { billToLocations, serviceLocations, runPestPacValidation } = params
  const { validatePostalCode } = useLocalisedValidations()

  const [billTos, someInvalidBillTos] = useMemo(
    () =>
      validateLocations(
        billToLocations,
        runPestPacValidation,
        validatePostalCode,
      ),
    [billToLocations, runPestPacValidation, validatePostalCode],
  )

  const [svcs, someInvalidSvcs] = useMemo(
    () =>
      validateLocations(
        serviceLocations,
        runPestPacValidation,
        validatePostalCode,
      ),
    [serviceLocations, runPestPacValidation, validatePostalCode],
  )

  const anyInvalid = someInvalidBillTos || someInvalidSvcs

  return {
    validatedBillToLocations: billTos,
    validatedServiceLocations: svcs,
    anyInvalid,
  }
}
